import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "sourceEmailInput",
    "sourceUserIdInput",
    "targetEmailInput",
    "targetUserIdInput",
    "sourceResults",
    "targetResults",
    "alertsList",
    "submitButton",
    "selectedCount"
  ]

  connect() {
    // Log form values when user tries to submit (for debugging)
    this.element.addEventListener('submit', (e) => {
      const checked = this.getCheckedAlerts()
      console.log(`Form submitted with ${checked.length} checked items`)
      checked.forEach(input => {
        console.log(`  - Checkbox value: "${input.value}"`)
      })
    })
  }

  #escapeDiv = document.createElement("div")
  #searchTimeout

  async search(event) {
    const type = event.target.dataset.type
    const query = event.target.value.trim()
    const resultsTarget = this.getResultsTarget(type)

    clearTimeout(this.#searchTimeout)

    if (query.length < 2) {
      resultsTarget.innerHTML = ""
      return
    }

    this.#searchTimeout = setTimeout(async () => {
      const users = await this.fetchUsers(query)
      this.renderResults(users, type)
    }, 300)
  }

  async fetchUsers(query) {
    try {
      const response = await fetch(
        `/admin/alert_transfers/search_users?q=${encodeURIComponent(query)}`
      )
      if (!response.ok) throw new Error("Failed to search users")
      return (await response.json()).users ?? []
    } catch (e) {
      console.error(e)
      return []
    }
  }

  selectUser(event) {
    event.preventDefault()
    const type = event.currentTarget.dataset.type
    const userId = event.currentTarget.dataset.userId
    const email = event.currentTarget.dataset.email

    this.getEmailInput(type).value = email
    this.getUserIdInput(type).value = userId
    this.getResultsTarget(type).innerHTML = ""

    if (type === "source") this.fetchSourceAlerts()
    this.updateSubmitState()
  }

  async fetchSourceAlerts() {
    const sourceUserId = this.sourceUserIdInputTarget.value
    if (!sourceUserId) return

    try {
      const response = await fetch(
        `/admin/alert_transfers/fetch_alerts?source_user_id=${sourceUserId}`
      )
      const data = await response.json()

      if (data.error) {
        this.alertsListTarget.innerHTML = `<p class="text-sentiment-negative">${this.escapeHtml(data.error)}</p>`
        return
      }

      this.renderAlerts(data.alerts)
    } catch (error) {
      this.alertsListTarget.innerHTML = `<p class="text-sentiment-negative">Error loading alerts</p>`
    }
  }

  renderResults(users, type) {
    const target = this.getResultsTarget(type)
    if (users.length === 0) {
      target.innerHTML = ''
      target.classList.remove('border', 'shadow-lg')
      return
    }

    const html = users
      .map(
        (user) => `
      <button type="button"
              data-action="click->alert-transfer#selectUser"
              data-type="${type}"
              data-user-id="${user.id}"
              data-email="${user.email}"
              class="w-full text-left block px-4 py-2 hover:bg-foot text-btn text-ink border-0 bg-transparent cursor-pointer">
        ${this.escapeHtml(user.email)}
      </button>
    `
      )
      .join("")

    target.innerHTML = html
    target.classList.add('border', 'shadow-lg')
  }

  renderAlerts(alerts) {
    if (alerts.length === 0) {
      this.alertsListTarget.innerHTML = '<p class="text-meta text-ink-slate">No active alerts found</p>'
      return
    }

    const html = alerts
      .map(
        (alert) => {
          const idValue = alert.type === "review_alert" ? `r-${alert.id}` : `a-${alert.id}`
          const badge = alert.type === "review_alert" ? '<span class="ml-2 text-meta text-ink-slate">[App Store]</span>' : ''
          return `
      <label class="flex items-start gap-3 p-3 border-b border-line hover:bg-foot cursor-pointer">
        <input type="checkbox"
               name="alert_ids[]"
               value="${idValue}"
               data-action="change->alert-transfer#updateSelectedCount"
               class="mt-1">
        <div class="flex-1">
          <div class="text-btn font-medium text-ink">
            ${this.escapeHtml(alert.name.toString())}${badge}
          </div>
          <div class="text-meta text-ink-slate">
            ${alert.mention_count} mentions · ${alert.platform_count} ${alert.platform_count === 1 ? 'platform' : 'platforms'}
          </div>
        </div>
      </label>
    `
        }
      )
      .join("")

    this.alertsListTarget.innerHTML = html
    this.updateSelectedCount()
  }

  updateSelectedCount() {
    const checked = this.getCheckedAlerts().length
    this.selectedCountTarget.textContent = checked
    this.updateSubmitState(checked)
  }

  updateSubmitState(checkedCount = null) {
    if (checkedCount === null) checkedCount = this.getCheckedAlerts().length

    const hasSource = this.sourceUserIdInputTarget.value.trim() !== ""
    const hasTarget = this.targetUserIdInputTarget.value.trim() !== ""

    this.submitButtonTarget.disabled = !hasSource || !hasTarget || checkedCount === 0
  }

  closeResults(event) {
    const type = event.target.dataset.type
    const resultsTarget = this.getResultsTarget(type)
    if (!resultsTarget.contains(event.relatedTarget)) {
      resultsTarget.innerHTML = ""
    }
  }

  preventBlur(event) {
    event.preventDefault()
  }

  escapeHtml(text) {
    this.#escapeDiv.textContent = text
    return this.#escapeDiv.innerHTML
  }

  getResultsTarget(type) {
    return type === "source" ? this.sourceResultsTarget : this.targetResultsTarget
  }

  getEmailInput(type) {
    return type === "source" ? this.sourceEmailInputTarget : this.targetEmailInputTarget
  }

  getUserIdInput(type) {
    return type === "source" ? this.sourceUserIdInputTarget : this.targetUserIdInputTarget
  }

  getCheckedAlerts() {
    return this.element.querySelectorAll('input[name="alert_ids[]"]:checked')
  }
}
